// Колоните на „По станции" — клиентски режим: отговорът носи целия период
// наведнъж (масив без страниране), затова сортирането, филтърът и редът
// „Общо" се смятат в браузъра.
//
// Колоните за методи на плащане се ГЕНЕРИРАТ от `methodEntries` — всички
// активни методи в реда от настройките, както в „По сервитьори". Смесеното
// своя колона няма: разпределено е в „В брой" и „С карта", затова „В брой" е
// и числото за сверка на касата.

import { useMemo } from "react";
import { createColumnHelper } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";

import { EM_DASH } from "../../../../components/dataTable";
import type { BarCellValue } from "../../../../components/dataTable";
import type { MethodEntry } from "../../../../utils/paymentMethodsDisplay";
import { DeltaChip } from "../ReportKpiCard";
import type { StationRow } from "./stationsMath";

const helper = createColumnHelper<StationRow>();

type Options = {
  /** Методите на плащане — по колона на метод, в реда на лентата. */
  methodEntries: MethodEntry[];
  /** Знаменателят на дяловете — оборотът на целия период. */
  totalRevenue: number;
  /** Сравнението е включено; иначе колоната Δ е само празни клетки. */
  showDelta: boolean;
  /** Потребителят има правото на справка „Сторно" (`view_voids`). */
  showVoids: boolean;
};

export const useStationsColumns = ({
  methodEntries,
  totalRevenue,
  showDelta,
  showVoids,
}: Options) => {
  const { t } = useTranslation();

  return useMemo(
    () => [
      helper.accessor((row) => row.stationName, {
        id: "station",
        header: t("reportsPage.station"),
        size: 200,
        meta: {
          cell: { kind: "text" },
          filter: { kind: "text" },
          alwaysVisible: true,
          mobileRole: "title",
          excel: { format: "text" },
        },
      }),

      // БЕЗ `total: "sum"`: числото е „сметки, докоснали тази станция", а една
      // сметка с ястие от кухнята и питие от бара влиза в ДВА реда. Сборът им
      // не е брой сметки за периода и при 80 сметки редът „Общо" щеше да
      // показва 150. Броят сметки не се получава от този отговор.
      helper.accessor((row) => row.bills, {
        id: "bills",
        header: t("reportsPage.stationsBillsColumn"),
        size: 104,
        meta: {
          cell: { kind: "num" },
          excel: { format: "number" },
        },
      }),

      ...methodEntries.map((method) =>
        helper.accessor((row) => row.methodAmounts.get(method.id) ?? 0, {
          id: `method_${method.id}`,
          header: method.label,
          size: 126,
          meta: {
            label: method.label,
            cell: { kind: "money", mutedZero: true },
            total: "sum",
            excel: { header: method.label, format: "money" },
          },
        }),
      ),

      helper.accessor((row) => row.revenue, {
        id: "revenue",
        header: t("reportsPage.stationsRevenueColumn"),
        size: 136,
        meta: {
          cell: { kind: "money", strong: true },
          total: "sum",
          alwaysVisible: true,
          mobileRole: "strong",
          excel: { format: "money" },
        },
      }),

      helper.accessor(
        (row): BarCellValue => ({
          value: row.revenue,
          max: totalRevenue,
          label: `${row.share.toFixed(1)} %`,
        }),
        {
          id: "share",
          header: t("reportsPage.stationsShareColumn"),
          size: 152,
          // Без този компаратор подредбата минава през общия „по показания
          // текст" за клетките-обекти и „9.5 %" застава над „10.1 %".
          sortingFn: (rowA, rowB) => rowA.original.share - rowB.original.share,
          meta: { cell: { kind: "bar" }, hideBelow: "narrow" },
        },
      ),

      ...(showVoids
        ? [
            helper.accessor((row) => row.voidTotal, {
              id: "voids",
              header: t("reportsPage.stationsVoidsColumn"),
              size: 124,
              meta: {
                cell: { kind: "money", negative: true },
                total: "sum",
                hideBelow: "narrow",
                excel: { format: "money" },
              },
            }),
          ]
        : []),

      ...(showDelta
        ? [
            helper.accessor((row) => row.delta, {
              id: "delta",
              header: t("reportsPage.stationsDeltaColumn"),
              size: 132,
              // Чипът е СОБСТВЕН рендер (`meta.cell` бие `cell` в таблицата),
              // защото зеленото/червеното идва от знака, не от тон в данните.
              cell: (context) => {
                const value = context.getValue();
                if (value === null || value === undefined) {
                  return <span className="text-faint">{EM_DASH}</span>;
                }
                return <DeltaChip value={value} />;
              },
              meta: { align: "right", hideBelow: "tablet" },
            }),
          ]
        : []),
    ],
    [t, methodEntries, totalRevenue, showDelta, showVoids],
  );
};
